import { editRequestWebSocket } from "@/aawebsocket/websocket.gen";
import { chatUpdateArtifact } from "@/api";
import { appState } from "@/app/appState";
import { ChatDocument, ChatEvent, chatState, ChatTag, type ChatArtifact as ChatArtifactObject } from "@/app/chatState";
import { artifact } from "@/app/typing/artifacts";
import { applyDiffs } from "@/components/diffs";
import { EntryItem, useRefState } from "@/components/UI/tools";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";
import { useRouteLoaderData } from "react-router-dom";
import { shortenLabel } from "../../chatObjectEditorWindow/chatObjectEditorWindow";
import { Org, User } from "@/components/UI/svgs";
import { set } from "mobx";
import { calendar_event_list_item } from "@/app/typing/events";
import { document_list_item } from "@/app/typing/documents";
import { decodeSource, encodeSource, sourceOptions } from "../../artifactSource";
import { tag_list_item } from "@/app/typing/tags";

function withIntentLabel(base: string, intent?: string): string {
    const trimmed = intent?.trim();
    return trimmed ? `${base} - ${trimmed}` : base;
}

const ChatArtifact = observer((params: { reload?: () => void, objKey: { type: "artifact", id: number }, tool_id?: string, revision_id?: number, intent?: string }) => {
    const [loading, setLoading] = useState(false);



    const [refresh, setRefresh] = useState(0);

    const [variableName, setVariableName] = useRefState<string>("");
    const [content, setContent] = useRefState<string | undefined>("");
    const [tagIds, setTagIds] = useRefState<number[]>([]);
    // Encoded source id: positive is an event, negative a document. See artifactSource.
    const [sourceId, setSourceId] = useRefState<number | undefined>(undefined);

    const identity = (edit: boolean | number | undefined) => { }
    const [ids, setIds] = useState<number[]>([]);
    const [id, setId] = useState<number>();
    const [revId, setRevId] = useState<number>(-1);
    const [msgId, setMsgId] = useState<number>();

    const [lock, setLock] = useRefState<boolean>(false);
    const [isDraft, setIsDraft] = useRefState<boolean | undefined>(undefined);

    const [tagSearch, setTagSearch] = useState<string>("")
    const [tagPageNumber, setTagPageNumber] = useState<number>(0)

    const [resContent, setResContent] = useRefState("")
    const [prevResContent, setPrevResContent] = useRefState<string | null>(null)
    const [diffTags, setDiffTags] = useState<{ added: number[], removed: number[] }>({ added: [], removed: [] });

    const passDiffToMark = (difftext: string) => {
        if (!difftext) return;

        const lines = difftext.split('\n');
        let markdown = '';

        let currentType: 'add' | 'remove' | 'context' | null = null;
        let currentBlockLines: string[] = [];

        const flushBlock = () => {
            if (currentBlockLines.length === 0) return;

            const content = currentBlockLines.join('\n');
            if (currentType === 'add') {
                markdown += `<div class="[&_p]:!m-0" style="background-color: #0f02; width: fit-content; padding: 0.4rem;">\n\n${content}\n\n</div>\n`;
            } else if (currentType === 'remove') {
                markdown += `<div class="[&_p]:!m-0" style="background-color: #f002; width: fit-content; padding: 0.4rem;">\n\n${content}\n\n</div>\n`;
            } else {
                markdown += `<div class="[&_p]:!m-0" style="opacity: 0.6; width: fit-content; padding: 0.4rem;">\n\n${content}\n\n</div>\n`;
            }
            currentBlockLines = [];
            currentType = null;
        };

        for (const line of lines) {
            if (line.startsWith('---') || line.startsWith('++') || line.startsWith("\\")) {
                continue;
            }

            if (line.startsWith('@@')) {
                flushBlock();
                // markdown += `------------------------------------\n`;
                continue;
            }

            let type: 'add' | 'remove' | 'context' | null = null;
            let content = '';

            if (line.startsWith('-')) {
                type = 'remove';
                content = line.substring(1);
            } else if (line.startsWith('+')) {
                type = 'add';
                content = line.substring(1);
            } else if (line.startsWith(' ')) {
                type = 'context';
                content = line;
            } else {
                continue;
            }

            if (type !== currentType) {
                flushBlock();
                currentType = type;
            }

            currentBlockLines.push(content);
        }
        flushBlock();
        
        console.log("Generated markdown with diffs", markdown);
        return markdown;
    }
    useEffect(() => {
        const obj = chatState.chatObjects.get(params.objKey)

        setVariableName(obj?.artifact?.variable_name ?? "")

        if (!obj) {
            params.reload?.();
            return;
        }

        if (params.revision_id && obj.artifact?.id) {
            const revisions_obj = [...obj?.revisions ?? []]?.sort((a, b) => a.id - b.id).filter(x => x?.id <= (params.revision_id ?? -1)).filter(x => x ? true : false) ?? []
            const revisions = revisions_obj.map(x => x?.content_diff)
            const { i, revObj } = revisions_obj?.map((x, i) => ({ i: i, revObj: x })).filter(x => x.revObj?.id == params.revision_id)[0] ?? { i: -1, revObj: null }
            setRevId(i)
            if (!revObj)
                return

            console.log("revision object tag ids", revObj.tag_ids as number[])

            setContent(passDiffToMark(revisions.at(-1) ?? ""))


            const currentTagIds = (revObj.tag_ids as number[]) ?? [];
            const prevTagIds = (i > 0 ? revisions_obj[i - 1]?.tag_ids as number[] : (obj?.artifact?.tag_ids as number[])) ?? [];

            const added = currentTagIds.filter(x => !prevTagIds.includes(x));
            const removed = prevTagIds.filter(x => !currentTagIds.includes(x));

            setDiffTags({ added, removed });
            setTagIds([...currentTagIds, ...removed]);
            // A revision carries the source the agent proposed, which may differ
            // from the artifact's current one.
            setSourceId(encodeSource(revObj as { event_id?: number | null; document_id?: number | null }))

            setLock(obj.artifact.user_lock ? !(obj.artifact.user_lock == appState.user?.id) : false)
            setId(obj.artifact.id)
            setMsgId(obj.msgId)
            setIsDraft(obj.artifact.is_draft)
        } else if ('content' in obj.artifact) {
            setContent(obj.artifact.content)
            setSourceId(encodeSource(obj.artifact))
            setTagIds(obj.artifact.tag_ids)
            setDiffTags({ added: [], removed: [] })
            setLock(obj.artifact.user_lock ? !(obj.artifact.user_lock == appState.user?.id) : false)
            setId(obj.artifact.id)
            setMsgId(obj.msgId)
            setIsDraft(obj.artifact.is_draft)
        }

        setRefresh(p => p + 1)
    }, [params.objKey, chatState.chatObjects.get(params.objKey)])


    useEffect(() => {
        setRefresh(p => p + 1)

    }, [prevResContent.current])

    useEffect(() => {
        tagIds.current?.forEach(tag_id => {
            if (!appState.tags?.find(t => t.id === tag_id) && !chatState.chatObjects.get({ type: "tag", id: tag_id })) {
                chatState.getObjectByKey({ type: "tag", id: tag_id });
            }
        });
    }, [tagIds.current]);

    useEffect(() => {
        const { event_id, document_id } = decodeSource(sourceId.current);
        if (event_id && !appState.events?.find(e => e.id === event_id) && !chatState.chatObjects.get({ type: "event", id: event_id })) {
            chatState.getObjectByKey({ type: "event", id: event_id });
        }
        if (document_id && !appState.documents?.find(d => d.id === document_id) && !chatState.chatObjects.get({ type: "document", id: document_id })) {
            chatState.getObjectByKey({ type: "document", id: document_id });
        }
    }, [sourceId.current]);



    return (
        <div id={`artifact${params.objKey.id}-${revId}`} title={`artifact${params.objKey.id}-${revId}`} className={`h-full w-full`}>
            <EntryItem
                lock={lock.current}
                setEdit={() => { }}

                headerClickNavigateFunction={(() => {
                    if (id) {
                        const revisionIndex = chatState.chatObjects.get({ type: "artifact", id: id })?.revisions?.filter(x => x != undefined)?.findIndex(x => x?.id == params.revision_id) != -1
                            ?
                            chatState.chatObjects.get({ type: "artifact", id: id })?.revisions?.filter(x => x != undefined)?.findIndex(x => x?.id == params.revision_id)
                            :
                            undefined

                        return () => chatState.openObject({ type: "artifact", id }, revisionIndex ?? -1);
                    }
                    return undefined
                })()}
                readonly={true}
                name={withIntentLabel((!params.revision_id) ? (isDraft.current ? appState.t("chat.tool.draftArtifact") : appState.t("objects.list.artifact")) : (id && id > 0) ? appState.t("chat.tool.artifactUpdate") : appState.t("chat.tool.entityOperation", { entity: appState.t("chat.objectType.artifact"), operation: "create" }), params.intent)}
                chatForm={true}
                loading={loading}
                deleting={false}
                refreshCount={refresh}
                idParams={{
                    singular: { get: id, set: identity },
                    plural: { get: ids, set: setIds },
                }}
                refColumns={

                    params.revision_id && (content.current?.length ?? 0) > 0 ? [
                        {
                            type: "richtext",
                            label: appState.t("objects.content"),
                            validator: (value: string | undefined) => (value ? true : false),
                            value: content.current,
                            onChange: params.revision_id ? () => { } : setContent,
                            artifactsComplete: true,
                        },
                    ]
                        :
                        undefined}
                nameColumn={{
                    type: "text",
                    value: variableName.current,
                    onChange: setVariableName,
                    validator: (val: string) => !/["'${}`,]/.test(val),
                    label: appState.t("objects.variableName"),
                }}
                stateColumns={[
                    {
                        label: "#",
                        type: "multiple",
                        onChange: setTagIds,
                        search: { get: tagSearch, set: setTagSearch },
                        pageNumber: { get: tagPageNumber, set: setTagPageNumber },
                        value: tagIds.current,
                        items: [...(appState.tags ?? []), ...chatState.chatObjects.entries().filter(x => x.value.type === "tag" && (x.value as ChatTag).tag && !(appState.tags ?? []).find(t => t.id === x.value.id)).map(x => (x.value as ChatTag).tag as any as tag_list_item)]?.map((x) => {
                            let className = undefined;
                            if (diffTags.added.includes(x.id)) className = "chip-added";
                            else if (diffTags.removed.includes(x.id)) className = "chip-removed";
                            return { id: x.id, name: x.name, className, children: (<div className="flex flex-row gap-[0.5rem] items-center"><>{shortenLabel(x.name)}</>{x.scope == 'me' ? <User size={16} /> : <Org size={16} />}</div>), defaultValue: tagIds.current?.includes(x.id) };

                        }),
                        validator: (val: number[]) => val && val.length > 0 ? true : false,
                    },
                    {
                        label: appState.t("objects.source"),
                        type: "single",
                        onChange: () => { },
                        search: undefined,
                        pageNumber: undefined,
                        hidden: !sourceId.current,
                        value: sourceId.current,
                        items: sourceOptions(
                            // Sources already open in the chat may not be in the paged list.
                            [...(appState.events ?? []), ...chatState.chatObjects.entries().filter(x => x.value.type === "event" && (x.value as ChatEvent).event && !(appState.events ?? []).find(e => e.id === x.value.id)).map(x => (x.value as ChatEvent).event as any as calendar_event_list_item)],
                            [...(appState.documents ?? []), ...chatState.chatObjects.entries().filter(x => x.value.type === "document" && (x.value as ChatDocument).document && !(appState.documents ?? []).find(d => d.id === x.value.id)).map(x => (x.value as ChatDocument).document as any as document_list_item)],
                            undefined,
                        ).map((x) => ({
                            id: x.id,
                            name: x.name,
                            children: (
                                <div className="flex flex-row gap-[0.5rem] items-center">
                                    <span className="chip-prefix">{appState.t(x.type === "document" ? "objects.list.document" : "objects.list.event")}</span>
                                    <>{shortenLabel(x.name)}</>
                                </div>
                            ),
                        })),
                        validator: () => true,
                    }
                ]}
            />
        </div>
    );
});

export default ChatArtifact